import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import { Add, Delete, Download, PictureAsPdf, UploadFile } from '@mui/icons-material';
import {
  docAssistantService,
  DocAssistantConversationSummary,
  DocAssistantDocument,
  DocAssistantMessage,
} from '../services/docAssistantService';
import { getApiError } from '../utils/apiError';

const TEAL = '#3A7380';

const DocAssistantPage: React.FC = () => {
  const [conversations, setConversations] = useState<DocAssistantConversationSummary[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [messages, setMessages] = useState<DocAssistantMessage[]>([]);
  const [input, setInput] = useState('');
  const [loadingList, setLoadingList] = useState(true);
  const [loadingConvo, setLoadingConvo] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Documents (inputs uploaded by the user + outputs produced by the assistant)
  const [documents, setDocuments] = useState<DocAssistantDocument[]>([]);
  const [uploadName, setUploadName] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Bumped to cancel any in-flight poll loop (on unmount or conversation switch)
  const pollTokenRef = useRef(0);

  useEffect(() => () => { pollTokenRef.current++; }, []);

  const loadDocuments = useCallback(async () => {
    try {
      setDocuments(await docAssistantService.listDocuments());
    } catch {
      /* non-fatal */
    }
  }, []);

  // Poll a running background job until it finishes. `token` is checked against
  // pollTokenRef so the loop stops if the user switches conversation / unmounts.
  const pollJob = useCallback(async (jobId: number, token: number) => {
    while (pollTokenRef.current === token) {
      await new Promise((r) => setTimeout(r, 2000));
      if (pollTokenRef.current !== token) return;
      try {
        const job = await docAssistantService.getJob(jobId);
        if (job.status === 'done') {
          if (job.message) setMessages((prev) => [...prev, job.message!]);
          loadDocuments();
          setSending(false);
          return;
        }
        if (job.status === 'error') {
          setError(job.error || 'Generation failed');
          setSending(false);
          return;
        }
      } catch (e) {
        setError(getApiError(e, 'Lost connection to the generation job'));
        setSending(false);
        return;
      }
    }
  }, [loadDocuments]);

  const loadConversations = useCallback(async () => {
    try {
      const list = await docAssistantService.listConversations();
      setConversations(list);
      return list;
    } catch (e) {
      setError(getApiError(e, 'Failed to load conversations'));
      return [];
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
    loadDocuments();
  }, [loadConversations, loadDocuments]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  const openConversation = async (id: number) => {
    const token = ++pollTokenRef.current; // cancel any in-flight poll from another conversation
    setSending(false);
    setActiveId(id);
    setLoadingConvo(true);
    setError(null);
    loadConversations(); // refresh running badges
    try {
      const convo = await docAssistantService.getConversation(id);
      setMessages(convo.messages || []);
      // If this conversation still has a generation running, show it and resume polling.
      if (convo.running_job_id && pollTokenRef.current === token) {
        setSending(true);
        pollJob(convo.running_job_id, token);
      }
    } catch (e) {
      setError(getApiError(e, 'Failed to load conversation'));
    } finally {
      setLoadingConvo(false);
    }
  };

  const handleNew = async () => {
    setError(null);
    try {
      const convo = await docAssistantService.createConversation();
      setConversations((prev) => [{ id: convo.id, title: convo.title }, ...prev]);
      setActiveId(convo.id);
      setMessages([]);
    } catch (e) {
      setError(getApiError(e, 'Failed to create conversation'));
    }
  };

  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await docAssistantService.deleteConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (activeId === id) {
        setActiveId(null);
        setMessages([]);
      }
    } catch (err) {
      setError(getApiError(err, 'Failed to delete conversation'));
    }
  };

  const handleSend = async () => {
    const content = input.trim();
    if (!content || sending) return;
    setError(null);

    let convoId = activeId;
    // Auto-create a conversation on first message
    if (!convoId) {
      try {
        const convo = await docAssistantService.createConversation();
        convoId = convo.id;
        setActiveId(convo.id);
        setConversations((prev) => [{ id: convo.id, title: convo.title }, ...prev]);
      } catch (e) {
        setError(getApiError(e, 'Failed to start conversation'));
        return;
      }
    }

    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content }]);
    setSending(true);
    loadConversations(); // title/order may update

    const token = ++pollTokenRef.current;
    let jobId: number;
    try {
      const res = await docAssistantService.sendMessage(convoId, content);
      jobId = res.job_id;
      loadConversations(); // reflect the running badge in the list
    } catch (e) {
      setError(getApiError(e, 'Failed to start generation'));
      setMessages((prev) => prev.filter((m) => !(m.role === 'user' && m.content === content)));
      setInput(content);
      setSending(false);
      return;
    }

    // The agent runs in a background job (broad questions can take minutes) — poll it.
    await pollJob(jobId, token);
  };

  const handleFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const name = uploadName.trim() || file.name.replace(/\.[^.]+$/, '');
    setUploading(true);
    setError(null);
    try {
      await docAssistantService.uploadDocument(name, file);
      setUploadName('');
      await loadDocuments();
    } catch (err) {
      setError(getApiError(err, 'Failed to upload document'));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDoc = async (id: number) => {
    try {
      await docAssistantService.deleteDocument(id);
      setDocuments((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      setError(getApiError(err, 'Failed to delete document'));
    }
  };

  const handleDownloadDoc = async (doc: DocAssistantDocument, format: 'docx' | 'pdf' = 'docx') => {
    try {
      await docAssistantService.downloadDocument(doc, format);
    } catch (err) {
      setError(getApiError(err, 'Failed to download document'));
    }
  };

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 120px)', gap: 2, p: 2 }}>
      {/* Sidebar: conversation list */}
      <Box sx={{ width: 280, display: 'flex', flexDirection: 'column', borderRight: '1px solid #E5F0F1' }}>
        <Button
          startIcon={<Add />}
          variant="contained"
          onClick={handleNew}
          sx={{ m: 1, backgroundColor: TEAL, textTransform: 'none', '&:hover': { backgroundColor: '#2e5c66' } }}
        >
          New conversation
        </Button>
        {loadingList ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <CircularProgress size={22} sx={{ color: TEAL }} />
          </Box>
        ) : (
          <List dense sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
            {conversations.map((c) => (
              <ListItemButton
                key={c.id}
                selected={c.id === activeId}
                onClick={() => openConversation(c.id)}
                sx={{ '&.Mui-selected': { backgroundColor: '#E5F0F1' } }}
              >
                {(c.running || (c.id === activeId && sending)) && (
                  <Tooltip title="Generating…">
                    <CircularProgress size={14} sx={{ color: TEAL, mr: 1, flexShrink: 0 }} />
                  </Tooltip>
                )}
                <Tooltip title={c.title || `Conversation #${c.id}`} enterDelay={500}>
                  <Typography noWrap sx={{ flex: 1, fontSize: '0.9rem', minWidth: 0 }}>
                    {c.title || `Conversation #${c.id}`}
                  </Typography>
                </Tooltip>
                <IconButton size="small" onClick={(e) => handleDelete(c.id, e)} aria-label="delete">
                  <Delete fontSize="small" />
                </IconButton>
              </ListItemButton>
            ))}
            {conversations.length === 0 && (
              <Typography variant="caption" sx={{ p: 2, color: '#888', display: 'block' }}>
                No conversations yet.
              </Typography>
            )}
          </List>
        )}

        {/* Documents: uploaded inputs + generated outputs */}
        <Box sx={{ borderTop: '1px solid #E5F0F1', pt: 1, mt: 1, display: 'flex', flexDirection: 'column', maxHeight: '45%' }}>
          <Typography variant="subtitle2" sx={{ px: 1, color: TEAL, fontWeight: 600 }}>
            Documents
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept=".txt,.md,.pdf,.doc,.docx,.csv,.json"
            onChange={handleFileChosen}
          />
          <Box sx={{ display: 'flex', gap: 0.5, p: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Name (optional)"
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              sx={{ flex: 1 }}
            />
            <Tooltip title="Upload a document to use as input">
              <span>
                <IconButton size="small" onClick={() => fileInputRef.current?.click()} disabled={uploading} sx={{ color: TEAL }}>
                  {uploading ? <CircularProgress size={18} sx={{ color: TEAL }} /> : <UploadFile fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <List dense sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
            {documents.map((d) => (
              <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.25 }}>
                <Chip
                  label={d.kind === 'output' ? 'out' : 'in'}
                  size="small"
                  color={d.kind === 'output' ? 'success' : 'default'}
                  sx={{ mr: 1, height: 18, fontSize: '0.65rem' }}
                />
                <Tooltip title={d.name} enterDelay={500}>
                  <Typography variant="caption" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.name}
                  </Typography>
                </Tooltip>
                <Tooltip title="Download as .docx">
                  <IconButton size="small" onClick={() => handleDownloadDoc(d, 'docx')}>
                    <Download sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download as .pdf">
                  <IconButton size="small" onClick={() => handleDownloadDoc(d, 'pdf')}>
                    <PictureAsPdf sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={() => handleDeleteDoc(d.id)}>
                  <Delete sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ))}
            {documents.length === 0 && (
              <Typography variant="caption" sx={{ px: 1, color: '#888', display: 'block' }}>
                No documents yet. Upload a source doc, or ask the assistant to produce one.
              </Typography>
            )}
          </List>
        </Box>
      </Box>

      {/* Main: chat thread */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" sx={{ color: TEAL, fontWeight: 600, mb: 1 }}>
          Documentation Assistant
        </Typography>
        <Typography variant="caption" sx={{ color: '#5A7380', mb: 1 }}>
          Ask for a functional spec or feature write-up. Answers are grounded in the app's code and live dev configuration.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

        <Box
          ref={scrollRef}
          sx={{ flex: 1, overflowY: 'auto', border: '1px solid #E5F0F1', borderRadius: '12px', p: 2, backgroundColor: '#FbFcFc' }}
        >
          {loadingConvo ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress size={24} sx={{ color: TEAL }} />
            </Box>
          ) : messages.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#999', textAlign: 'center', mt: 6 }}>
              Start by describing the feature or spec you need.
            </Typography>
          ) : (
            messages.map((m, i) => (
              <Box key={i} sx={{ mb: 2, display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <Box
                  sx={{
                    maxWidth: '85%',
                    p: 1.5,
                    borderRadius: '12px',
                    backgroundColor: m.role === 'user' ? TEAL : 'white',
                    color: m.role === 'user' ? 'white' : '#263238',
                    border: m.role === 'user' ? 'none' : '1px solid #E5F0F1',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '0.92rem',
                    fontFamily: m.role === 'assistant' ? '"SFMono-Regular", Menlo, monospace' : 'inherit',
                    lineHeight: 1.5,
                  }}
                >
                  {m.content}
                </Box>
              </Box>
            ))
          )}
          {sending && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: TEAL }}>
              <CircularProgress size={16} sx={{ color: TEAL }} />
              <Typography variant="caption">Researching code &amp; config…</Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            maxRows={6}
            placeholder="e.g. Write a functional spec for the patient questionnaire email-verification flow"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            disabled={sending}
          />
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={sending || !input.trim()}
            sx={{ backgroundColor: TEAL, textTransform: 'none', minWidth: 90, '&:hover': { backgroundColor: '#2e5c66' } }}
          >
            Send
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default DocAssistantPage;
